import React from 'react';
import { toast } from 'react-toastify';
import SearchableDropdown from '../Subscription/SearchableDropdown';
import { getAllOrganizationNames } from '../../Services/OrganizationService';
import { getAllPayers, getOrganizationProviders } from '../../Services/Claim360Service';

// ~3,700 payers: render them in pages so the menu stays responsive.
const PAYER_PAGE_SIZE = 50;

function useLoaded(loader, token, errorMessage) {
  const [options, setOptions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    setLoading(true);
    loader(token)
      .then((response) => {
        if (!cancelled) setOptions(response?.data ?? []);
      })
      .catch((error) => {
        console.error(errorMessage, error);
        if (!cancelled) toast.error(error.message || errorMessage);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [loader, token, errorMessage]);

  return [options, loading];
}

const unwrapPayers = async (token) => {
  const response = await getAllPayers(token);
  return { data: response?.data?.payers ?? [] };
};

// SearchableDropdown bound to a Formik field; touching on change shows errors without a blur event.
function FormikDropdown({ formik, name, ...props }) {
  const error = formik.touched[name] && formik.errors[name];
  return (
    <SearchableDropdown
      {...props}
      value={formik.values[name]}
      onChange={(value) => {
        formik.setFieldTouched(name, true, false);
        formik.setFieldValue(name, value ?? '');
      }}
      error={error || ''}
    />
  );
}

export function OrganizationSelect({ formik, name = 'organizationId', token, label = 'Organization' }) {
  const [organizations, loading] = useLoaded(getAllOrganizationNames, token, 'Unable to load organizations.');
  const options = React.useMemo(
    () => organizations.map((organization) => ({ value: organization.id, label: organization.group_office_name })),
    [organizations],
  );

  return (
    <FormikDropdown
      formik={formik}
      name={name}
      label={label}
      placeholder="Select organization"
      options={options}
      loading={loading}
      disabled={loading}
      showSearch
      required
      emptyMessage="No organizations found"
    />
  );
}

// Providers of the selected organization (only those with an NPI). Cascades from the organization field:
// the list reloads when the organization changes, and a provider that is not in it is cleared.
export function ProviderSelect({ formik, name = 'providerId', organizationName = 'organizationId', token, label = 'Provider' }) {
  const organizationId = formik.values[organizationName];
  const [providers, setProviders] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const { setFieldValue } = formik;
  const selected = formik.values[name];

  React.useEffect(() => {
    setProviders([]);
    if (!token || !organizationId) return undefined;
    let cancelled = false;
    setLoading(true);
    getOrganizationProviders(organizationId, token)
      .then((response) => {
        if (!cancelled) setProviders(response?.data ?? []);
      })
      .catch((error) => {
        console.error('Unable to load providers.', error);
        if (!cancelled) toast.error(error.message || 'Unable to load providers.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [organizationId, token]);

  // A provider picked under another organization is not valid for this one.
  React.useEffect(() => {
    if (!loading && selected && !providers.some((provider) => String(provider.id) === String(selected))) {
      setFieldValue(name, '', false);
    }
  }, [loading, providers, selected, name, setFieldValue]);

  const options = React.useMemo(
    () => providers.map(({ id, name: providerName, npi }) => ({
      value: id,
      label: `${providerName} (NPI ${npi})`,
      searchText: `${providerName} ${npi}`,
    })),
    [providers],
  );

  let placeholder = 'Select provider';
  if (!organizationId) placeholder = 'Select an organization first';
  else if (loading) placeholder = 'Loading providers...';

  return (
    <FormikDropdown
      formik={formik}
      name={name}
      label={label}
      placeholder={placeholder}
      options={options}
      loading={loading}
      disabled={!organizationId || loading}
      showSearch
      required
      emptyMessage="No providers with an NPI in this organization"
    />
  );
}

export function usePayers(token) {
  return useLoaded(unwrapPayers, token, 'Unable to load payers.');
}

// One option per payer, labelled with its display name and also found by any of its aliases.
export const payerOptions = (payers) => payers.map(({ name, primaryPayerId, aliases = [] }) => {
  const label = `${name} (${primaryPayerId})`;
  return { value: primaryPayerId, label, searchText: [label, ...aliases].join(' | ') };
});

export function PayerSelect({ formik, name, payers, loading, label = 'Insurance Payer', required = true }) {
  const options = React.useMemo(() => payerOptions(payers), [payers]);

  return (
    <FormikDropdown
      formik={formik}
      name={name}
      label={label}
      placeholder={loading ? 'Loading payers...' : 'Search payer by name or ID'}
      options={options}
      loading={loading}
      disabled={loading}
      pageSize={PAYER_PAGE_SIZE}
      showSearch
      required={required}
      emptyMessage="No payers found"
    />
  );
}
